import { SurveyResponse, SurveyRoutingStep } from "types/surveyTest.types"
import { AcceptedScreen, pageTitle } from "./session"

/** A screen shown on the way: its title, and the length of the routing trace when it showed. */
export type RoutingPage = Pick<AcceptedScreen, "title" | "traceLength">

/** The routing decisions that led to one page, or to the end. */
export interface RoutingGroup {
  /** The page's title, "the end" or "the terminate" */
  to: string
  steps: SurveyRoutingStep[]
}

/** Where the latest answer led: the page shown, the end or the terminate. */
export const routingTarget = (response: SurveyResponse): string =>
  response.status === "end"
    ? "the end"
    : response.status === "terminated"
      ? "the terminate"
      : pageTitle(response.screen.cards) || "this page"

/**
 * The trace grouped by the screen each decision led to. The session's trace only grows, so the decisions
 * before screen i showed and after screen i-1 did led to screen i; the rest led to `last`, where the latest
 * answer went. Only groups with decisions are listed.
 */
export const routingGroups = (trace: SurveyRoutingStep[], pages: RoutingPage[], last: string): RoutingGroup[] => {
  const groups: RoutingGroup[] = []
  let start = 0
  for (const page of pages) {
    const end = Math.min(Math.max(page.traceLength, start), trace.length)
    groups.push({ to: page.title, steps: trace.slice(start, end) })
    start = end
  }
  groups.push({ to: last, steps: trace.slice(start) })
  return groups.filter((g) => g.steps.length > 0)
}

/** How a decision's badge looks: the house green, a quiet grey, the danger red or amber. */
export type Tone = "yes" | "no" | "terminated" | "skipped"

/** A decision's badge: "Yes" or "No", "Terminated" for a terminate taken, "Skipped" for a skip taken. */
export const outcomeOf = (step: SurveyRoutingStep): { text: string; tone: Tone } | undefined => {
  if (step.result === undefined) return undefined
  if (!step.result) return { text: "No", tone: "no" }
  const kind = step.kind.trim().toUpperCase()
  if (kind === "TERMINATE") return { text: "Terminated", tone: "terminated" }
  if (kind === "SKIP TO") return { text: "Skipped", tone: "skipped" }
  return { text: "Yes", tone: "yes" }
}
